import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { extractAttributes } from '@/utils/api-client';
import {
  skillTemplateService,
  type SkillTemplateCreateData,
  type SkillTemplateUpdateData,
} from '@/services/skill-template-service';
import {
  Header,
  ContentContainer,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Button,
  Spinner,
  Empty,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  toast,
} from 'prosci-ui';
import { usePageContext } from '@/contexts/page-context';
import { Save, RefreshCw } from '@/lib/icons';
import type { SkillFormData, FileSection } from './skill-form';
import { TemplateDetailsCard } from './template-details-card';
import { TriggerConfigCard } from './trigger-config-card';
import { SkillFilesCard } from './skill-files-card';
import { SkillFolderPreview } from './skill-folder-preview';
import { VersionHistoryCard } from './version-history-card';

export function SkillTemplateEditorPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setShowPageHeader } = usePageContext();

  const [form, setForm] = useState<SkillFormData>({
    name: '',
    display_title: '',
    description: '',
    skill_md_content: '',
    skill_type: 'interactive',
    execution_mode: 'conversational',
    trigger_patterns: '',
    trigger_keywords: '',
    negative_patterns: '',
    execution_config: {},
    document_skills: '',
    is_active: true,
    scripts: {},
    references: {},
  });
  const [changeDescription, setChangeDescription] = useState('');
  const [showRollbackConfirm, setShowRollbackConfirm] = useState<number | null>(null);
  const [showAdvancedTriggers, setShowAdvancedTriggers] = useState(false);
  const [expandedFiles, setExpandedFiles] = useState<Set<string>>(new Set());

  useEffect(() => {
    setShowPageHeader(false);
    return () => setShowPageHeader(true);
  }, [setShowPageHeader]);

  // Fetch existing template
  const { data: templateData, isLoading } = useQuery({
    queryKey: ['skillTemplate', id],
    queryFn: () => skillTemplateService.get(id!),
    enabled: !isNew,
  });

  // Fetch versions
  const { data: versions } = useQuery({
    queryKey: ['skillTemplateVersions', id],
    queryFn: () => skillTemplateService.getVersions(id!),
    enabled: !isNew,
  });

  // Populate form from fetched data
  useEffect(() => {
    if (templateData) {
      const t = extractAttributes(templateData.data);
      if (t) {
        setForm({
          name: t.name || '',
          display_title: t.display_title || '',
          description: t.description || '',
          skill_md_content: t.skill_md_content || '',
          skill_type: t.skill_type || 'interactive',
          execution_mode: t.execution_mode || 'conversational',
          trigger_patterns: (t.trigger_config?.patterns || []).join('\n'),
          trigger_keywords: (t.trigger_config?.keywords || []).join(', '),
          negative_patterns: (t.trigger_config?.negative_patterns || []).join('\n'),
          execution_config: t.execution_config || {},
          document_skills: (t.document_skills || []).join(', '),
          is_active: t.is_active ?? true,
          scripts: t.scripts || {},
          references: t.references || {},
        });
        // Auto-expand advanced section if regex patterns exist
        const hasPatterns = (t.trigger_config?.patterns || []).length > 0;
        const hasNegatives = (t.trigger_config?.negative_patterns || []).length > 0;
        if (hasPatterns || hasNegatives) {
          setShowAdvancedTriggers(true);
        }
      }
    }
  }, [templateData]);

  const createMutation = useMutation({
    mutationFn: (data: SkillTemplateCreateData) => skillTemplateService.create(data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['skillTemplates'] });
      toast.success('Skill template created');
      const newId = result?.data?.id;
      if (newId) {
        navigate(`/kaiya-skills/${newId}/edit`);
      } else {
        navigate('/kaiya-skills');
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to create skill template');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ data, desc }: { data: SkillTemplateUpdateData; desc?: string }) =>
      skillTemplateService.update(id!, data, desc),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['skillTemplate', id] });
      queryClient.invalidateQueries({ queryKey: ['skillTemplates'] });
      queryClient.invalidateQueries({ queryKey: ['skillTemplateVersions', id] });
      toast.success('Skill template updated');
      setChangeDescription('');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update skill template');
    },
  });

  const rollbackMutation = useMutation({
    mutationFn: (versionNumber: number) => skillTemplateService.rollback(id!, versionNumber),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['skillTemplate', id] });
      queryClient.invalidateQueries({ queryKey: ['skillTemplateVersions', id] });
      queryClient.invalidateQueries({ queryKey: ['skillTemplates'] });
      toast.success('Rolled back successfully');
      setShowRollbackConfirm(null);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to rollback');
    },
  });

  const syncMutation = useMutation({
    mutationFn: () => skillTemplateService.sync(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['skillTemplate', id] });
      toast.success('Synced to Anthropic');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to sync');
    },
  });

  const buildPayload = () => {
    const patterns = form.trigger_patterns.split('\n').map(s => s.trim()).filter(Boolean);
    const keywords = form.trigger_keywords.split(',').map(s => s.trim()).filter(Boolean);
    const negatives = form.negative_patterns.split('\n').map(s => s.trim()).filter(Boolean);
    const docSkills = form.document_skills.split(',').map(s => s.trim()).filter(Boolean);

    return {
      name: form.name,
      display_title: form.display_title,
      description: form.description,
      skill_md_content: form.skill_md_content,
      skill_type: form.skill_type,
      execution_mode: form.execution_mode,
      trigger_config: {
        patterns: patterns.length ? patterns : undefined,
        keywords: keywords.length ? keywords : undefined,
        negative_patterns: negatives.length ? negatives : undefined,
      },
      execution_config: form.execution_config,
      document_skills: docSkills.length ? docSkills : [],
      is_active: form.is_active,
      scripts: Object.keys(form.scripts).length ? form.scripts : {},
      references: Object.keys(form.references).length ? form.references : {},
    };
  };

  const handleSave = () => {
    const payload = buildPayload();
    if (isNew) {
      createMutation.mutate(payload);
    } else {
      updateMutation.mutate({
        data: payload,
        desc: changeDescription || undefined,
      });
    }
  };

  const updateField = <K extends keyof SkillFormData>(field: K, value: SkillFormData[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  // --- File management helpers (scripts & references) ---
  const toggleFileExpanded = (key: string) => {
    setExpandedFiles((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const getNextFilename = (section: FileSection) => {
    const defaultExt = section === 'scripts' ? 'py' : 'md';
    const prefix = section === 'scripts' ? 'script' : 'reference';
    let i = 1;
    while (`${prefix}-${i}.${defaultExt}` in form[section]) i++;
    return `${prefix}-${i}.${defaultExt}`;
  };

  const addFile = (section: FileSection) => {
    const filename = getNextFilename(section);
    setForm((prev) => ({
      ...prev,
      [section]: { ...prev[section], [filename]: '' },
    }));
    setExpandedFiles((prev) => new Set(prev).add(`${section}:${filename}`));
  };

  const removeFile = (section: FileSection, filename: string) => {
    setForm((prev) => {
      const updated = { ...prev[section] };
      delete updated[filename];
      return { ...prev, [section]: updated };
    });
    setExpandedFiles((prev) => {
      const next = new Set(prev);
      next.delete(`${section}:${filename}`);
      return next;
    });
  };

  const renameFile = (section: FileSection, oldName: string, newName: string) => {
    const trimmed = newName.trim();
    if (!trimmed || trimmed === oldName) return;
    if (trimmed in form[section]) {
      toast.error(`"${trimmed}" already exists`);
      return;
    }
    setForm((prev) => {
      const entries = Object.entries(prev[section]);
      const updated: Record<string, string> = {};
      for (const [key, val] of entries) {
        updated[key === oldName ? trimmed : key] = val;
      }
      return { ...prev, [section]: updated };
    });
    setExpandedFiles((prev) => {
      const next = new Set(prev);
      next.delete(`${section}:${oldName}`);
      next.add(`${section}:${trimmed}`);
      return next;
    });
  };

  const updateFileContent = (section: FileSection, filename: string, content: string) => {
    setForm((prev) => ({
      ...prev,
      [section]: { ...prev[section], [filename]: content },
    }));
  };

  const handleFileUpload = (section: FileSection, files: FileList | null) => {
    if (!files) return;
    Array.from(files).forEach((file) => {
      if (file.name in form[section]) {
        toast.error(`File "${file.name}" already exists in ${section}`);
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const content = reader.result as string;
        // Re-check inside the updater: `form` above is a snapshot from
        // upload time, but other reads may have landed since then.
        setForm((prev) => {
          if (file.name in prev[section]) return prev;
          return { ...prev, [section]: { ...prev[section], [file.name]: content } };
        });
        setExpandedFiles((prev) => new Set(prev).add(`${section}:${file.name}`));
      };
      reader.readAsText(file);
    });
  };

  if (!isNew && isLoading) {
    return (
      <Empty>
        <Spinner />
      </Empty>
    );
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <>
      <Header
        title={isNew ? 'New Skill Template' : `Edit: ${form.display_title || form.name}`}
        description="Define a SKILL.md template following the Agent Skills open standard"
      >
        <div className="flex gap-2">
          {!isNew && (
            <Button
              variant="outline"
              onClick={() => syncMutation.mutate()}
              disabled={syncMutation.isPending}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Sync to Anthropic
            </Button>
          )}
          <Button onClick={handleSave} disabled={isSaving} className="bg-white text-primary hover:bg-white/90">
            <Save className="h-4 w-4 mr-2" />
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </Header>

      <ContentContainer>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main editor */}
          <div className="lg:col-span-2 space-y-6">
            <TemplateDetailsCard form={form} updateField={updateField} />

            <TriggerConfigCard
              form={form}
              updateField={updateField}
              showAdvanced={showAdvancedTriggers}
              onShowAdvancedChange={setShowAdvancedTriggers}
            />

            <Card>
              <CardHeader>
                <CardTitle>SKILL.md Content</CardTitle>
                <CardDescription>
                  The main instruction body of the skill template. This follows the Agent Skills
                  open standard format.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <textarea
                  className="w-full px-3 py-2 border rounded-md text-sm font-mono"
                  rows={20}
                  placeholder={`# Change Management Plan Builder\n\n## Purpose\nGuide the user through creating a comprehensive change management plan...\n\n## Steps\n1. Identify the change...\n2. Assess impact...\n3. Build the plan...`}
                  value={form.skill_md_content}
                  onChange={(e) => updateField('skill_md_content', e.target.value)}
                />
              </CardContent>
            </Card>

            <SkillFilesCard
              section="scripts"
              title="Scripts"
              description="Executable scripts that the AI can run. These are placed in the scripts/ directory of the skill folder."
              files={form.scripts}
              accept=".py,.sh,.bash,.rb,.js,.ts,.sql,.txt,.md,.json,.yaml,.yml"
              addLabel="Add Script"
              emptyText="No scripts yet. Add a new script or upload files from disk."
              expandedFiles={expandedFiles}
              onToggleExpanded={toggleFileExpanded}
              onAdd={() => addFile('scripts')}
              onRemove={(filename) => removeFile('scripts', filename)}
              onRename={(oldName, newName) => renameFile('scripts', oldName, newName)}
              onUpdateContent={(filename, content) => updateFileContent('scripts', filename, content)}
              onUpload={(files) => handleFileUpload('scripts', files)}
            />

            <SkillFilesCard
              section="references"
              title="Reference Documents"
              description="Reference materials like examples, schemas, or documentation. These are placed in the references/ directory of the skill folder and loaded on-demand."
              files={form.references}
              accept=".md,.sql,.txt,.json,.yaml,.yml,.xml,.csv,.py,.sh,.rb,.js,.ts"
              addLabel="Add Reference"
              emptyText="No references yet. Add a new reference or upload files from disk."
              expandedFiles={expandedFiles}
              onToggleExpanded={toggleFileExpanded}
              onAdd={() => addFile('references')}
              onRemove={(filename) => removeFile('references', filename)}
              onRename={(oldName, newName) => renameFile('references', oldName, newName)}
              onUpdateContent={(filename, content) => updateFileContent('references', filename, content)}
              onUpload={(files) => handleFileUpload('references', files)}
            />

            {!isNew && (
              <Card>
                <CardHeader>
                  <CardTitle>Change Description</CardTitle>
                  <CardDescription>
                    Describe what changed in this update (optional, stored with version history)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border rounded-md text-sm"
                    placeholder="Updated step 3 to include stakeholder analysis"
                    value={changeDescription}
                    onChange={(e) => setChangeDescription(e.target.value)}
                  />
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <SkillFolderPreview form={form} />

            {!isNew && (
              <VersionHistoryCard
                versions={versions}
                onRollbackClick={setShowRollbackConfirm}
              />
            )}
          </div>
        </div>
      </ContentContainer>

      {/* Rollback confirmation */}
      <Dialog
        open={showRollbackConfirm !== null}
        onOpenChange={() => setShowRollbackConfirm(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rollback to Version {showRollbackConfirm}</DialogTitle>
            <DialogDescription>
              This will revert the skill template content to version {showRollbackConfirm} and
              create a new version entry. Provider sync status will be reset to pending.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRollbackConfirm(null)}>
              Cancel
            </Button>
            <Button
              onClick={() =>
                showRollbackConfirm !== null &&
                rollbackMutation.mutate(showRollbackConfirm)
              }
              disabled={rollbackMutation.isPending}
            >
              {rollbackMutation.isPending ? 'Rolling back...' : 'Confirm Rollback'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
